import { ignoreCancelledNavigation } from '@/lib/ignore-cancelled-navigation';
import { useWorkflowTemplateVersion } from '@commenda/api';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/** Routing + data for the affiliate workflow editor: the version under edit (SWR), the
 * `?conversationId=` builder-chat param, and version hops that stay on the affiliate route. */
export const useAffiliateWorkflowEditor = () => {
  const router = useRouter();
  const firmId = typeof router.query.id === 'string' ? router.query.id : '';
  const versionId =
    typeof router.query.versionId === 'string' ? router.query.versionId : '';
  const conversationId =
    typeof router.query.conversationId === 'string'
      ? router.query.conversationId
      : null;

  const { data, error, isLoading, mutate } = useWorkflowTemplateVersion(
    versionId || null
  );
  const version = data?.workflowTemplateVersion ?? null;
  /** Re-reads the version so the status badge follows a publish or a make-live. */
  const refreshVersion = useCallback(() => {
    mutate().catch((refreshError) =>
      console.error('Could not refresh the workflow version:', refreshError)
    );
  }, [mutate]);

  const setConversationParam = useCallback(
    (nextConversationId: string | null) => {
      const query: Record<string, string> = {};
      if (nextConversationId) query.conversationId = nextConversationId;
      router
        .replace(
          {
            pathname: `/affiliate/${firmId}/workflows/version/${versionId}`,
            query,
          },
          undefined,
          { shallow: true }
        )
        .catch(ignoreCancelledNavigation);
    },
    [router, firmId, versionId]
  );

  // Clearing the param is enough: the panel creates a conversation lazily on the first send.
  const onStartNewConversation = useCallback(
    () => setConversationParam(null),
    [setConversationParam]
  );

  const onNavigateToVersion = useCallback(
    (targetVersionId: string) => {
      if (targetVersionId === versionId) return;
      router
        .push(
          `/affiliate/${firmId}/workflows/version/${targetVersionId}${
            conversationId ? `?conversationId=${conversationId}` : ''
          }`
        )
        .catch(ignoreCancelledNavigation);
    },
    [router, firmId, versionId, conversationId]
  );

  return {
    version,
    error,
    firmId,
    conversationId,
    setConversationParam,
    onStartNewConversation,
    onNavigateToVersion,
    refreshVersion,
    // The router query is empty on the first render, so a missing versionId is still "loading",
    // never an error.
    isLoading: isLoading || !versionId,
  };
};
